import { useCallback, useEffect, useState } from 'react';
import { PlatformRole, type AdminUserListResult, type AdminUserRow } from 'minka-shared';
import * as adminApi from '@/services/admin-api';
import { describeAdminError } from '@/utils/api-error';

const EMPTY: AdminUserListResult = { rows: [], truncated: false, activeSuperadminCount: 0 };

/** §3.1 invariant 6 counts these, and only these. */
function isActiveSuperadmin(row: AdminUserRow | undefined): boolean {
  return !!row && row.isActive && row.platformRole === PlatformRole.SUPERADMIN;
}

export interface UseAdminUsersResult {
  data: AdminUserListResult;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** The account a mutation is running for, for per-row spinners. */
  pendingUserId: string | null;
  deactivate: (userId: string) => Promise<void>;
  reactivate: (userId: string) => Promise<void>;
  setPlatformRole: (userId: string, platformRole: PlatformRole) => Promise<void>;
  /** Tombstone a deactivated account's address; the server picks it. */
  changeEmail: (userId: string) => Promise<void>;
  clearError: () => void;
}

/**
 * The console's Users tab (§4.3): the searched list, and the four things a
 * superadmin does to an account.
 *
 * Every mutation answers with the account's rebuilt row, so the row is replaced
 * in place rather than refetched — the server has already applied the rules
 * (a deactivated user holds no active permission, so their island count and
 * organizations change with the flag) and re-reading the list would only make
 * the table blink.
 */
export function useAdminUsers(search: string): UseAdminUsersResult {
  const [data, setData] = useState<AdminUserListResult>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      setData(await adminApi.listUsers(search));
      setError(null);
    } catch (loadError) {
      setError(describeAdminError(loadError, 'Failed to load users'));
    } finally {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const applyRow = useCallback((row: AdminUserRow) => {
    setData(previous => {
      // The count is the platform's, not the page's, so it is kept in step by
      // what the mutation did rather than recounted from the rows in hand: a
      // search may be hiding most of them.
      const before = isActiveSuperadmin(previous.rows.find(existing => existing.id === row.id));
      const after = isActiveSuperadmin(row);

      return {
        ...previous,
        rows: previous.rows.map(existing => (existing.id === row.id ? row : existing)),
        activeSuperadminCount:
          previous.activeSuperadminCount + (after ? 1 : 0) - (before ? 1 : 0)
      };
    });
  }, []);

  /** Run one account mutation, keeping the row, the spinner and the error in step. */
  const mutate = useCallback(
    async (userId: string, action: () => Promise<AdminUserRow>, fallback: string) => {
      setPendingUserId(userId);
      setError(null);
      try {
        applyRow(await action());
      } catch (mutationError) {
        setError(describeAdminError(mutationError, fallback));
        throw mutationError;
      } finally {
        setPendingUserId(null);
      }
    },
    [applyRow]
  );

  const deactivate = useCallback(
    (userId: string) =>
      mutate(userId, () => adminApi.deactivateUser(userId), 'Failed to deactivate the account'),
    [mutate]
  );

  const reactivate = useCallback(
    (userId: string) =>
      mutate(userId, () => adminApi.reactivateUser(userId), 'Failed to reactivate the account'),
    [mutate]
  );

  const setPlatformRole = useCallback(
    (userId: string, platformRole: PlatformRole) =>
      mutate(
        userId,
        () => adminApi.setPlatformRole(userId, platformRole),
        'Failed to change the platform role'
      ),
    [mutate]
  );

  const changeEmail = useCallback(
    (userId: string) =>
      mutate(
        userId,
        () => adminApi.changeUserEmail(userId),
        'Failed to change the email address'
      ),
    [mutate]
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    data,
    isLoading,
    error,
    refresh,
    pendingUserId,
    deactivate,
    reactivate,
    setPlatformRole,
    changeEmail,
    clearError
  };
}
